/**
 * GRANULLO EXPOSURE ENGINE — React Context
 * 
 * Provides exposure calculation state and methods throughout the application.
 * This engine reveals structural exposure, not predictions.
 * All outputs are advisory and non-executing.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import {
  calculateExposureProfile,
  calculateNetworkExposureState,
  calculateExposureDelta,
  getExposureColor,
  getExposureClassification
} from '../utils/ExposureCalculator';

// Context
const ExposureEngineContext = createContext(null);

// Hook for consuming the context
export function useExposureEngine() {
  const context = useContext(ExposureEngineContext);
  if (!context) {
    throw new Error('useExposureEngine must be used within an ExposureEngineProvider');
  }
  return context;
}

// Provider component
export function ExposureEngineProvider({ children }) {
  // State
  const [exposureProfiles, setExposureProfiles] = useState(new Map());
  const [networkState, setNetworkState] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [lastCalculated, setLastCalculated] = useState(null);
  
  /**
   * Calculate exposure for a single commitment
   */
  const calculateCommitmentExposure = useCallback((commitment) => {
    const profile = calculateExposureProfile(commitment);
    setExposureProfiles(prev => {
      const next = new Map(prev);
      next.set(commitment.id || commitment._id, profile);
      return next;
    });
    return profile;
  }, []);
  
  /**
   * Calculate exposure for multiple commitments (batch)
   */
  const calculateBatchExposure = useCallback((commitments) => {
    setIsCalculating(true);
    
    const profiles = new Map();
    commitments.forEach(commitment => {
      const profile = calculateExposureProfile(commitment);
      profiles.set(commitment.id || commitment._id, profile);
    });
    
    setExposureProfiles(profiles);
    
    // Calculate network state
    const profilesArray = Array.from(profiles.values());
    const netState = calculateNetworkExposureState(profilesArray);
    setNetworkState(netState);
    setLastCalculated(new Date().toISOString());
    setIsCalculating(false);
    
    return { profiles, networkState: netState };
  }, []);
  
  /**
   * Get exposure profile for a specific commitment
   */
  const getExposureProfile = useCallback((commitmentId) => {
    return exposureProfiles.get(commitmentId) || null;
  }, [exposureProfiles]);
  
  /**
   * Get all exposure profiles as array
   */
  const getAllProfiles = useCallback(() => {
    return Array.from(exposureProfiles.values());
  }, [exposureProfiles]);
  
  /**
   * Get top N commitments by exposure
   */
  const getTopExposureCommitments = useCallback((n = 5) => {
    const profiles = Array.from(exposureProfiles.values());
    return profiles
      .sort((a, b) => b.exposureScore - a.exposureScore)
      .slice(0, n);
  }, [exposureProfiles]);
  
  /**
   * Calculate exposure delta for simulation
   */
  const calculateSimulationDelta = useCallback((simulationParams) => {
    if (!networkState) return null;
    
    // Simulate modified exposure based on simulation parameters
    const modifiedProfiles = Array.from(exposureProfiles.values()).map(profile => {
      // Apply simulation effects
      let scoreDelta = 0;
      
      if (simulationParams.scenario === 'cascade_failure') {
        scoreDelta = Math.random() * 15 + 5;
      } else if (simulationParams.scenario === 'dependency_removal') {
        scoreDelta = -(Math.random() * 10 + 2);
      } else if (simulationParams.scenario === 'stress_test') {
        scoreDelta = Math.random() * 20 - 5;
      }
      
      return {
        ...profile,
        exposureScore: Math.max(0, Math.min(100, profile.exposureScore + scoreDelta)),
        economicExposure: Math.max(0, profile.economicExposure * (1 + scoreDelta / 100))
      };
    });
    
    const afterState = calculateNetworkExposureState(modifiedProfiles);
    return calculateExposureDelta(networkState, afterState);
  }, [networkState, exposureProfiles]);
  
  /**
   * Refresh all exposure calculations
   */
  const refreshAllExposure = useCallback((commitments) => {
    return calculateBatchExposure(commitments);
  }, [calculateBatchExposure]);
  
  // ============================================================================
  // PHASE 4: GOVERNANCE EXPOSURE EVALUATION
  // FOUNDER DIRECTIVE: Translate structural weaknesses and signal drift into governance exposure
  // ============================================================================
  
  // State for Phase 4 governance exposure
  const [governanceExposure, setGovernanceExposure] = useState({
    concentration: 0,
    authorityBottleneck: 0,
    policyMisuse: 0,
    monitoringBlindSpot: 0,
    regionalInstability: 0,
    systemicOverload: 0
  });
  
  /**
   * Calculate governance exposure from blueprint and signals
   * FOUNDER DIRECTIVE: Exposure must be explainable and attributable to real structures
   */
  const calculateGovernanceExposure = useCallback((blueprintGraph, signalStates, decisionBacklog) => {
    const exposure = {
      concentration: 0,
      authorityBottleneck: 0,
      policyMisuse: 0,
      monitoringBlindSpot: 0,
      regionalInstability: 0,
      systemicOverload: 0,
      details: {}
    };
    
    if (!blueprintGraph?.nodes) {
      setGovernanceExposure(exposure);
      return exposure;
    }
    
    // 1. Concentration Exposure
    // Check if too much flow goes through few nodes
    const edgeCounts = {};
    blueprintGraph.edges?.forEach(edge => {
      edgeCounts[edge.target] = (edgeCounts[edge.target] || 0) + 1;
    });
    const maxConcentration = Math.max(0, ...Object.values(edgeCounts));
    const totalEdges = blueprintGraph.edges?.length || 1;
    exposure.concentration = Math.min(100, (maxConcentration / totalEdges) * 200);
    exposure.details.concentration = {
      maxConcentration,
      totalEdges,
      description: `Top node receives ${maxConcentration} of ${totalEdges} flows`
    };
    
    // 2. Authority Bottleneck Exposure
    // Check if authorities are overloaded
    const authorityNodes = blueprintGraph.nodes.filter(n => 
      ['authority_node', 'regulator', 'sponsor'].includes(n.type)
    );
    const nonAuthorityNodes = blueprintGraph.nodes.length - authorityNodes.length;
    const authorityRatio = authorityNodes.length > 0 ? nonAuthorityNodes / authorityNodes.length : 100;
    exposure.authorityBottleneck = Math.min(100, Math.max(0, (authorityRatio - 5) * 10));
    exposure.details.authorityBottleneck = {
      authorityCount: authorityNodes.length,
      nonAuthorityCount: nonAuthorityNodes,
      ratio: Math.round(authorityRatio * 10) / 10,
      description: `${authorityNodes.length} authorities for ${nonAuthorityNodes} participants`
    };
    
    // 3. Monitoring Blind Spot Exposure
    // Check signals coverage of nodes
    const signalArray = signalStates ? Array.from(signalStates.values()) : [];
    const monitoredNodes = new Set(signalArray.map(s => s.nodeRef).filter(Boolean));
    const totalNodes = blueprintGraph.nodes.length;
    const coverage = totalNodes > 0 ? (monitoredNodes.size / totalNodes) * 100 : 0;
    exposure.monitoringBlindSpot = Math.max(0, 100 - coverage);
    exposure.details.monitoringBlindSpot = {
      monitoredNodes: monitoredNodes.size,
      totalNodes,
      coverage: Math.round(coverage),
      description: `${Math.round(coverage)}% node coverage`
    };
    
    // 4. Systemic Overload Exposure
    // Based on decision backlog and signal health
    const pendingDecisions = decisionBacklog?.pending || 0;
    const overdueDecisions = decisionBacklog?.overdue || 0;
    const degradedSignals = signalArray.filter(s => s.health?.status === 'degraded' || s.health?.status === 'critical').length;
    exposure.systemicOverload = Math.min(100, pendingDecisions * 5 + overdueDecisions * 15 + degradedSignals * 10);
    exposure.details.systemicOverload = {
      pendingDecisions,
      overdueDecisions,
      degradedSignals,
      description: `${overdueDecisions} overdue, ${degradedSignals} degraded signals`
    };
    
    // 5. Regional/Cluster Instability
    // Group nodes by layer and check balance
    const layerCounts = {};
    blueprintGraph.nodes.forEach(n => {
      const layer = n.layer || 'default';
      layerCounts[layer] = (layerCounts[layer] || 0) + 1;
    });
    const layerValues = Object.values(layerCounts);
    const avgLayerSize = layerValues.reduce((a, b) => a + b, 0) / layerValues.length;
    const layerImbalance = layerValues.reduce((sum, v) => sum + Math.abs(v - avgLayerSize), 0) / layerValues.length;
    exposure.regionalInstability = Math.min(100, layerImbalance * 10);
    exposure.details.regionalInstability = {
      layerCount: layerValues.length,
      avgLayerSize: Math.round(avgLayerSize),
      imbalance: Math.round(layerImbalance * 10) / 10,
      description: `${layerValues.length} layers, avg size ${Math.round(avgLayerSize)}`
    };
    
    // 6. Policy Misuse Exposure (placeholder - would need policy data)
    exposure.policyMisuse = 0;
    exposure.details.policyMisuse = { description: 'No policy violations detected' };
    
    // Calculate overall exposure score
    const weights = { concentration: 0.2, authorityBottleneck: 0.2, policyMisuse: 0.1, monitoringBlindSpot: 0.2, regionalInstability: 0.15, systemicOverload: 0.15 };
    exposure.overallScore = Math.round(
      exposure.concentration * weights.concentration +
      exposure.authorityBottleneck * weights.authorityBottleneck +
      exposure.policyMisuse * weights.policyMisuse +
      exposure.monitoringBlindSpot * weights.monitoringBlindSpot +
      exposure.regionalInstability * weights.regionalInstability +
      exposure.systemicOverload * weights.systemicOverload
    );
    
    setGovernanceExposure(exposure);
    return exposure;
  }, []);
  
  // Memoized context value
  const contextValue = useMemo(() => ({
    // State
    exposureProfiles,
    networkState,
    isCalculating,
    lastCalculated,
    governanceExposure, // Phase 4
    
    // Methods
    calculateCommitmentExposure,
    calculateBatchExposure,
    getExposureProfile,
    getAllProfiles,
    getTopExposureCommitments,
    calculateSimulationDelta,
    refreshAllExposure,
    calculateGovernanceExposure, // Phase 4
    
    // Utilities
    getExposureColor,
    getExposureClassification
  }), [
    exposureProfiles,
    networkState,
    isCalculating,
    lastCalculated,
    governanceExposure,
    calculateCommitmentExposure,
    calculateBatchExposure,
    getExposureProfile,
    getAllProfiles,
    getTopExposureCommitments,
    calculateSimulationDelta,
    refreshAllExposure,
    calculateGovernanceExposure
  ]);
  
  return (
    <ExposureEngineContext.Provider value={contextValue}>
      {children}
    </ExposureEngineContext.Provider>
  );
}

export default ExposureEngineContext;
